import { useCallback, useEffect, useRef } from 'react';

const ITEM_COUNT = 110;

const WIDTH = 21;
const HEIGHT = 13;

/// Onde a conversa vira lead: antes é balão de mensagem, depois é card no funil.
const CONVERSION = 0.42;

/// Duração da transformação, em progresso; curta, pois a graça é o estalo.
const MORPH = 0.07;

const LANES = 9;
const FUNNEL = 0.72;

const PUSH_RADIUS = 150;
const PUSH_FORCE = 55;
const RETURN_SPEED = 0.05;

type Color = [number, number, number];

/// Cores de cada porta de entrada: são as marcas dos canais, para reconhecer de onde o contato veio.
const CHANNELS: Color[] = [
    [37, 211, 102],   // WhatsApp
    [214, 41, 118],   // Instagram
    [148, 163, 184],  // Site
    [96, 165, 250],   // E-mail
];

const COLD: Color = [59, 130, 246];
const WARM: Color = [245, 158, 11];
const HOT: Color = [239, 68, 68];

interface Item {
    channel: Color;
    progress: number;
    speed: number;
    lane: number;

    /// Onde este lead se perde; sempre depois da conversão (conversa não evapora, lead sim).
    outgoing: number;

    z: number;
    sway: number;
    dx: number;
    dy: number;
}

export interface ChannelFlowCanvasProps {
    className?: string;
    style?: React.CSSProperties;
    rightInset?: number;
}

const mix = (from: Color, to: Color, t: number): Color => [
    Math.round(from[0] + (to[0] - from[0]) * t),
    Math.round(from[1] + (to[1] - from[1]) * t),
    Math.round(from[2] + (to[2] - from[2]) * t),
];

/// Depois de virar lead ele esquenta até o fim do funil.
const temperature = (t: number): Color =>
    t < 0.5 ? mix(COLD, WARM, t / 0.5) : mix(WARM, HOT, (t - 0.5) / 0.5);

const newItem = (initialProgress: number): Item => {
    const z = 0.35 + Math.random() * 0.65;

    return {
        channel: CHANNELS[Math.floor(Math.random() * CHANNELS.length)],
        progress: initialProgress,
        speed: (0.00035 + Math.random() * 0.0007) * (0.5 + z),
        lane: (Math.floor(Math.random() * LANES) - (LANES - 1) / 2) / ((LANES - 1) / 2),
        outgoing: Math.random() < 0.5
            ? CONVERSION + MORPH + Math.random() * (1 - CONVERSION - MORPH)
            : 1,
        z,
        sway: Math.random() * Math.PI * 2,
        dx: 0,
        dy: 0,
    };
};

/// Fundo do login: a conversa que chega por um canal virando lead no funil.
/// Balão entra pela esquerda na cor do canal, estala no meio e vira card, que esquenta e afunila.
export const ChannelFlowCanvas: React.FC<ChannelFlowCanvasProps> = ({
    className,
    style,
    rightInset = 0,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const mouseRef = useRef({ x: -9999, y: -9999 });
    const itemsRef = useRef<Item[]>([]);
    const rafRef = useRef<number>(0);
    const timeRef = useRef(0);

    const start = useCallback(() => {
        itemsRef.current = Array.from({ length: ITEM_COUNT }, () => newItem(Math.random()));
    }, []);

    const resize = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const dpr = window.devicePixelRatio || 1;

        canvas.width = window.innerWidth * dpr;
        canvas.height = window.innerHeight * dpr;
    }, []);

    const onMove = useCallback((e: MouseEvent) => {
        mouseRef.current = { x: e.clientX, y: e.clientY };
    }, []);

    const onSignOut = useCallback(() => {
        mouseRef.current = { x: -9999, y: -9999 };
    }, []);

    /// Um desenho para os dois estados: m vai de 0 (balão) a 1 (card) e tudo é interpolado entre eles.
    const draw = (
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        scale: number,
        color: Color,
        alpha: number,
        m: number,
    ) => {
        const w = WIDTH * scale;
        const h = HEIGHT * scale;

        // Balão tem canto redondo e rabicho; card é reto e tem bolinha.
        const radius = (6.5 - 3 * m) * scale;

        ctx.beginPath();
        ctx.roundRect(x - w / 2, y - h / 2, w, h, radius);
        ctx.fillStyle = `rgba(18,24,42,${alpha * 0.85})`;
        ctx.fill();

        ctx.lineWidth = Math.max(0.6, scale);
        ctx.strokeStyle = `rgba(${color[0]},${color[1]},${color[2]},${alpha})`;
        ctx.stroke();

        if (m < 1) {
            const tail = (1 - m) * 4 * scale;

            ctx.beginPath();
            ctx.moveTo(x - w / 2 + 3 * scale, y + h / 2);
            ctx.lineTo(x - w / 2 + 1 * scale, y + h / 2 + tail);
            ctx.lineTo(x - w / 2 + 7 * scale, y + h / 2);
            ctx.closePath();
            ctx.fillStyle = `rgba(18,24,42,${alpha * 0.85})`;
            ctx.fill();
            ctx.strokeStyle = `rgba(${color[0]},${color[1]},${color[2]},${alpha * (1 - m)})`;
            ctx.stroke();
        }

        if (m > 0) {
            const dotRadius = Math.max(0.9, 1.7 * scale);

            ctx.beginPath();
            ctx.arc(
                x - w / 2 + dotRadius + 2.5 * scale,
                y - h / 2 + dotRadius + 2.5 * scale,
                dotRadius,
                0,
                Math.PI * 2,
            );
            ctx.fillStyle = `rgba(${color[0]},${color[1]},${color[2]},${alpha * m})`;
            ctx.fill();
        }

        // O tracinho do texto existe nos dois: mensagem no balão, nome no card.
        ctx.beginPath();
        ctx.roundRect(
            x - w / 2 + 3 * scale,
            y + h / 2 - 4.2 * scale,
            w * (0.58 - 0.12 * (1 - m)),
            Math.max(0.8, 1.4 * scale),
            1,
        );
        ctx.fillStyle = `rgba(226,232,240,${alpha * 0.5})`;
        ctx.fill();
    };

    const animate = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;
        const w = canvas.width / dpr;
        const h = canvas.height / dpr;

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);

        const util = w - rightInset;
        const margin = util * 0.08;
        const width = util - margin * 2;
        const cy = h * 0.5;
        const funnelHeight = h * 0.4;

        const mx = mouseRef.current.x;
        const my = mouseRef.current.y;

        timeRef.current += 0.016;
        const t = timeRef.current;

        for (let i = 0; i < itemsRef.current.length; i++) {
            const item = itemsRef.current[i];

            item.progress += item.speed;

            if (item.progress >= Math.min(item.outgoing + 0.08, 1)) {
                itemsRef.current[i] = newItem(0);
                continue;
            }

            const p = item.progress;

            // Quanto já virou lead; antes da conversão é 0 e o funil nem começou a estreitar.
            const m = Math.min(1, Math.max(0, (p - CONVERSION) / MORPH));
            const inFunnel = Math.max(0, (p - CONVERSION) / (1 - CONVERSION));

            const baseX = margin + p * width;
            const baseY = cy
                + item.lane * funnelHeight * (1 - FUNNEL * inFunnel)
                + Math.sin(t * 0.5 + item.sway) * 9 * item.z;

            const ddx = baseX + item.dx - mx;
            const ddy = baseY + item.dy - my;
            const dist = Math.hypot(ddx, ddy);

            if (dist < PUSH_RADIUS && dist > 0) {
                const force = ((1 - dist / PUSH_RADIUS) ** 2) * PUSH_FORCE;
                item.dx += (ddx / dist) * force * 0.1;
                item.dy += (ddy / dist) * force * 0.1;
            }

            item.dx *= 1 - RETURN_SPEED;
            item.dy *= 1 - RETURN_SPEED;

            const x = baseX + item.dx;
            const y = baseY + item.dy;

            const inbound = Math.min(1, p / 0.08);
            const end = Math.min(item.outgoing, 1);
            const outgoing = Math.min(1, Math.max(0, (end - p) / 0.08));
            const alpha = (0.25 + item.z * 0.55) * inbound * outgoing;

            if (alpha <= 0.02) continue;

            const color = m < 1
                ? mix(item.channel, COLD, m)
                : temperature(inFunnel);

            const scale = 0.65 + item.z * 0.6;

            // O estalo da conversão: anel que abre e some quando a conversa vira lead.
            if (m > 0 && m < 1) {
                const ring = WIDTH * scale * (0.8 + m * 1.6);

                ctx.beginPath();
                ctx.arc(x, y, ring, 0, Math.PI * 2);
                ctx.strokeStyle = `rgba(${color[0]},${color[1]},${color[2]},${alpha * (1 - m) * 0.5})`;
                ctx.lineWidth = Math.max(0.6, scale);
                ctx.stroke();
            }

            if (item.z > 0.7) {
                const glow = ctx.createRadialGradient(x, y, 0, x, y, WIDTH * scale * 1.6);

                glow.addColorStop(0, `rgba(${color[0]},${color[1]},${color[2]},${alpha * 0.22})`);
                glow.addColorStop(1, 'rgba(0,0,0,0)');

                ctx.beginPath();
                ctx.arc(x, y, WIDTH * scale * 1.6, 0, Math.PI * 2);
                ctx.fillStyle = glow;
                ctx.fill();
            }

            draw(ctx, x, y, scale, color, alpha, m);
        }

        rafRef.current = requestAnimationFrame(animate);
    }, [rightInset]);

    useEffect(() => {
        start();
        resize();

        window.addEventListener('resize', resize);
        window.addEventListener('mousemove', onMove);
        document.addEventListener('mouseleave', onSignOut);

        rafRef.current = requestAnimationFrame(animate);

        return () => {
            cancelAnimationFrame(rafRef.current);
            window.removeEventListener('resize', resize);
            window.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseleave', onSignOut);
        };
    }, [start, resize, onMove, onSignOut, animate]);

    return (
        <canvas
            ref={canvasRef}
            className={className}
            style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                ...style,
            }}
        />
    );
};
